import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { archiveQuote, deleteQuote, unarchiveQuote } from "../actions/quoteLifecycle";

const toMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) return (error.response?.data as any)?.message || error.message || fallback;
  if (error instanceof Error) return error.message;
  return fallback;
};

const notifyQuotesChanged = (quoteId: number) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("flownect:quotes-updated", { detail: { quoteId } }));
};

/**
 * Mutations for the Quote lifecycle actions (archive / unarchive / delete). Each mutation
 * invalidates the `quotes` react-query cache and dispatches `flownect:quotes-updated` so the
 * `useQuotes` hook (state-based) also refetches.
 */
export const useQuoteActions = (quoteId: number) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useGlobalSnackbar();

  const onSettled = () => {
    queryClient.invalidateQueries({ queryKey: ["quotes"] });
    notifyQuotesChanged(quoteId);
  };

  const archiveMutation = useMutation({
    mutationFn: () => archiveQuote(quoteId),
    onSuccess: () => {
      showSnackbar("Quote archived", "success", 3000, "top-center");
      onSettled();
    },
    onError: error => showSnackbar(toMessage(error, "Could not archive quote"), "error", 6000, "top-center"),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveQuote(quoteId),
    onSuccess: () => {
      showSnackbar("Quote restored", "success", 3000, "top-center");
      onSettled();
    },
    onError: error => showSnackbar(toMessage(error, "Could not restore quote"), "error", 6000, "top-center"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteQuote(quoteId),
    onSuccess: () => {
      showSnackbar("Quote deleted", "success", 3000, "top-center");
      onSettled();
    },
    onError: error => showSnackbar(toMessage(error, "Could not delete quote"), "error", 6000, "top-center"),
  });

  return {
    archive: archiveMutation.mutate,
    unarchive: unarchiveMutation.mutate,
    remove: deleteMutation.mutate,
    isBusy: archiveMutation.isPending || unarchiveMutation.isPending || deleteMutation.isPending,
  };
};
